import { useCallback, useEffect, useRef, useState } from 'react';
import { overcallingDataEditingApi } from '../api/overcallingDataEditingApi';
import type {
  OvercallingCandidateItem,
  OvercallingCurationRecord,
  OvercallingFilter,
} from '../types/overcallingDataEditing';

// Last curation-action type, drives the confirmation message color + the
// green answer-container background (FR-025 / FR-030 / FR-031).
//
// Session 2026-05-20 scope reduction: `isProblem` discriminator removed because
// teaching problems are OUT OF SCOPE for curation — every item is now a hand.
export type OvercallingLastAction = null | {
  kind: 'Keep' | 'Reject';
};

// FR-030a (Session 2026-05-27; delay shortened to 1s on 2026-05-28, then to 500ms on
// 2026-05-29 after manual testing): after a successful Keep / Don't-Keep the page holds the
// confirmation message for this many milliseconds — with all five action buttons disabled and
// the gray content container covered by the auto-advance mask overlay — then automatically
// advances to the next item (exactly as a Next click would). The timed advance is NOT
// user-cancellable and there is no disable toggle (it is short, non-destructive, and
// reversible via Previous on the next load).
const AUTO_ADVANCE_DELAY_MS = 500;

interface HookState {
  filter: OvercallingFilter;
  items: OvercallingCandidateItem[];
  currentIndex: number;
  // Running counter under "Hand: N/Total". Keep/Reject leaves the item in the
  // working set and Total stays constant (FR-038 Session 2026-04-23). Only
  // filter change or Refresh re-computes counterTotal.
  counterTotal: number;
  counterPosition: number;
  loading: boolean;
  // FR-030a: true during the 1-second post-curation confirmation window, before the
  // timed auto-advance fires. The page disables all five action buttons while either
  // `loading` (network in-flight, FR-031b) or `autoAdvancing` is true, and shows the
  // auto-advance mask overlay over the gray content container.
  autoAdvancing: boolean;
  error: string | null;
  lastAction: OvercallingLastAction;
}

const INITIAL_STATE: HookState = {
  filter: 'NotEdited',
  items: [],
  currentIndex: 0,
  counterTotal: 0,
  counterPosition: 0,
  loading: false,
  autoAdvancing: false,
  error: null,
  lastAction: null,
};

// Pure index advance shared by manual Next and the FR-030a timed auto-advance: move to the
// next item (wrapping from the last back to the first per FR-018) and clear the confirmation.
function advanceState(prev: HookState): HookState {
  if (prev.items.length === 0) return { ...prev, lastAction: null };
  const nextIndex = prev.currentIndex + 1;
  if (nextIndex >= prev.items.length) {
    return { ...prev, currentIndex: 0, counterPosition: 1, lastAction: null };
  }
  return { ...prev, currentIndex: nextIndex, counterPosition: nextIndex + 1, lastAction: null };
}

export function useOvercallingDataEditingSession() {
  const [state, setState] = useState<HookState>(INITIAL_STATE);
  const autoAdvanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Monotonic id stamped on each loadCandidates call. Only the response of the
  // most-recently-started load is applied; earlier in-flight loads are ignored
  // when they resolve. This guards against (a) React 18 StrictMode double-invoking
  // the mount effect — which fires two concurrent NotEdited loads, the later of
  // which would otherwise resolve after a curation click and wipe the in-memory
  // working set back to a fresh, uncurated pool — and (b) the real-user race of
  // rapidly switching the Data-category filter or double-clicking Refresh, where
  // a slower earlier response must not overwrite the latest selection.
  const loadIdRef = useRef(0);

  const clearAutoAdvance = useCallback(() => {
    if (autoAdvanceTimer.current !== null) {
      clearTimeout(autoAdvanceTimer.current);
      autoAdvanceTimer.current = null;
    }
  }, []);

  // FR-030a: hold the confirmation for AUTO_ADVANCE_DELAY_MS, then auto-advance and
  // re-enable the action buttons (autoAdvancing -> false). The setState reads the latest
  // state at fire time, so it advances from wherever the curation left the index.
  const scheduleAutoAdvance = useCallback(() => {
    clearAutoAdvance();
    autoAdvanceTimer.current = setTimeout(() => {
      autoAdvanceTimer.current = null;
      setState(prev => ({ ...advanceState(prev), autoAdvancing: false }));
    }, AUTO_ADVANCE_DELAY_MS);
  }, [clearAutoAdvance]);

  // Cancel any pending auto-advance when the component unmounts.
  useEffect(() => () => clearAutoAdvance(), [clearAutoAdvance]);

  const loadCandidates = useCallback(async (filter: OvercallingFilter) => {
    clearAutoAdvance();
    const myLoadId = ++loadIdRef.current;
    setState(prev => ({ ...prev, loading: true, error: null, lastAction: null, autoAdvancing: false }));
    try {
      const response = await overcallingDataEditingApi.getCandidates(filter);
      // Drop the response if a newer load has since started (StrictMode double-mount,
      // or a rapid filter/Refresh change) — applying it would clobber the latest state.
      if (loadIdRef.current !== myLoadId) return;
      // Session 2026-05-20: the server returns items sorted by tableresult_id
      // ascending (FR-018) so the editor sees the same order on every visit and
      // can trace which hands they've already curated. No client-side shuffle.
      const items = response.items;
      setState({
        filter,
        items,
        currentIndex: 0,
        counterTotal: items.length,
        counterPosition: items.length > 0 ? 1 : 0,
        loading: false,
        autoAdvancing: false,
        error: null,
        lastAction: null,
      });
    } catch (err) {
      if (loadIdRef.current !== myLoadId) return;
      setState(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load candidates',
      }));
    }
  }, [clearAutoAdvance]);

  // Initial load of the default filter.
  useEffect(() => {
    loadCandidates('NotEdited');
  }, [loadCandidates]);

  const reconcileCurrentItem = useCallback((record: OvercallingCurationRecord) => {
    setState(prev => {
      const updated = prev.items.slice();
      updated[prev.currentIndex] = {
        ...updated[prev.currentIndex]!,
        curation: record,
      };
      return { ...prev, items: updated };
    });
  }, []);

  // FR-030a (Session 2026-05-27): Keep / Don't-Keep upsert the current item's curation row
  // and leave the working-set list, currentIndex, and counterTotal untouched — the item
  // stays in the snapshot so Previous can return to it (FR-038). On success the confirmation
  // shows, all five action buttons stay disabled (autoAdvancing), and after
  // AUTO_ADVANCE_DELAY_MS the page auto-advances to the next item (wrapping per FR-018). On a
  // failed save no auto-advance is scheduled and the buttons re-enable for retry (FR-031a).
  const applyKeep = useCallback(async () => {
    clearAutoAdvance();
    const current = state.items[state.currentIndex];
    if (!current) return;
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const record = await overcallingDataEditingApi.keepHand(current.tableResultId);
      setState(prev => {
        const newItems = prev.items.slice();
        newItems[prev.currentIndex] = { ...current, curation: record };
        return {
          ...prev,
          items: newItems,
          loading: false,
          autoAdvancing: true,
          lastAction: { kind: 'Keep' },
        };
      });
      scheduleAutoAdvance();
    } catch (err) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to save curation',
      }));
    }
  }, [state.items, state.currentIndex, clearAutoAdvance, scheduleAutoAdvance]);

  const applyReject = useCallback(async () => {
    clearAutoAdvance();
    const current = state.items[state.currentIndex];
    if (!current) return;
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const record = await overcallingDataEditingApi.rejectHand(current.tableResultId);
      setState(prev => {
        const newItems = prev.items.slice();
        newItems[prev.currentIndex] = { ...current, curation: record };
        return {
          ...prev,
          items: newItems,
          loading: false,
          autoAdvancing: true,
          lastAction: { kind: 'Reject' },
        };
      });
      scheduleAutoAdvance();
    } catch (err) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to save curation',
      }));
    }
  }, [state.items, state.currentIndex, clearAutoAdvance, scheduleAutoAdvance]);

  const advanceNext = useCallback(() => {
    setState(prev => advanceState(prev));
  }, []);

  const advancePrev = useCallback(() => {
    setState(prev => {
      if (prev.items.length === 0) return { ...prev, lastAction: null };
      const prevIndex = prev.currentIndex - 1;
      if (prevIndex < 0) {
        // Wrap to the end so Previous mirrors Next's wrap behaviour.
        const lastIndex = prev.items.length - 1;
        return { ...prev, currentIndex: lastIndex, counterPosition: lastIndex + 1, lastAction: null };
      }
      return {
        ...prev,
        currentIndex: prevIndex,
        counterPosition: prevIndex + 1,
        lastAction: null,
      };
    });
  }, []);

  const currentItem = state.items[state.currentIndex] ?? null;

  return {
    // state
    filter: state.filter,
    items: state.items,
    currentItem,
    currentIndex: state.currentIndex,
    counterTotal: state.counterTotal,
    counterPosition: state.counterPosition,
    loading: state.loading,
    autoAdvancing: state.autoAdvancing,
    error: state.error,
    lastAction: state.lastAction,

    // actions
    loadCandidates,
    applyKeep,
    applyReject,
    advanceNext,
    advancePrev,
    reconcileCurrentItem,
  };
}
